import React from 'react'
import {
  CheckOutlined,
  ClockCircleOutlined,
  DislikeOutlined,
  ExceptionOutlined,
  HourglassOutlined,
  InboxOutlined,
  EyeOutlined,
  BarChartOutlined,
  LoginOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  StopOutlined,
  SyncOutlined,
  UserAddOutlined,
} from '@ant-design/icons'

import { Can, useTranslation } from '@nic/commons'
import { Div, Flex, PaperLayout, VerticalLayout } from '@nic/ui-comps'

import { IRegistrarInfo } from '../types'
import { LayoutContext } from '../Context/LayoutContext'
import { Breadcrumb } from '../Components/Breadcrumb/Breadcrumb'
import { DashboardSectionTitle } from '../Components/DashboardSectionTitle'
import { DashboardCounter } from '../Components/Counters/DashboardCounter'
import { IAssignmentParam } from '../Components/Counters/types'
import { NicScope } from '@nic/nic-api/types'
import { useAuth } from '@nic/kc-auth'
import { isRegistrarUserDarwin } from '../Components/User/utils'
import './DashboardPage.css'

interface IDashboard {
  realm: NicScope
}

const DashboardColorMapping: any = {
  workingSection: {
    background: 'rgb(255, 185, 128)',
    activeIconColor: '#d98e06',
    disabledIconColor: '#f1daaf',
  },
  successSection: {
    background: 'rgb(151, 181, 82)',
    activeIconColor: 'white',
  },
  rejectOrFailureSection: {
    background: 'rgb(216, 122, 128)',
    activeIconColor: 'white',
  },
  processingExpiredSection: {
    background: 'rgb(121, 198, 242)',
    activeIconColor: 'white',
  },
  processingSection: {
    background: 'rgb(121, 198, 242)',
    activeIconColor: 'white',
  },
  searchSection: {
    background: 'rgb(115, 185, 188) ',
    activeIconColor: 'white',
  },
  initialSection: {
    background: 'rgb(141, 152, 179)',
    activeIconColor: 'white',
  },
  expiringSection: {
    background: 'rgb(255, 75, 75)',
    activeIconColor: 'white',
  },
}

interface MappingI {
  icons: Record<string, React.ReactNode>
  background: Record<string, string>
}

const PlayOrPause: Record<string, MappingI> = {
  registry: {
    icons: {
      pending: (
        <>
          <HourglassOutlined style={{ fontSize: '2.2rem', color: 'white' }} />
        </>
      ),
      pendingAssigned: (
        <>
          <PauseCircleOutlined style={{ fontSize: '2.2rem', color: 'white' }} />
          <UserAddOutlined style={{ fontSize: '1.8rem', color: 'white' }} />
        </>
      ),
      registrarUpdated: <PlayCircleOutlined style={{ fontSize: '2.2rem', color: 'white' }} />,
      registrarUpdatedAssigned: (
        <>
          <PlayCircleOutlined style={{ fontSize: '2.2rem', color: 'white' }} />
          <UserAddOutlined style={{ fontSize: '1.8rem', color: 'white' }} />
        </>
      ),
    },
    background: {
      pending: 'rgb(255, 150, 80)',
      pendingAssigned: 'rgb(255, 165, 13)',
      registrarUpdated: 'rgb(64, 169, 255)',
      registrarUpdatedAssigned: 'rgb(24, 144, 255)',
    },
  },
  registrar: {
    icons: {
      pending: <PlayCircleOutlined style={{ fontSize: '2.2rem', color: 'white' }} />,
      registrarUpdated: <HourglassOutlined style={{ fontSize: '2.2rem', color: 'white' }} />,
      pendingAssigned: (
        <>
          <PlayCircleOutlined style={{ fontSize: '2.2rem', color: 'white' }} />
          <UserAddOutlined style={{ fontSize: '1.8rem', color: 'white' }} />
        </>
      ),
      registrarUpdatedAssigned: (
        <>
          <PauseCircleOutlined style={{ fontSize: '2.2rem', color: 'white' }} />
          <UserAddOutlined style={{ fontSize: '1.8rem', color: 'white' }} />
        </>
      ),
    },
    background: {
      registrarUpdated: 'rgb(255, 150, 80)',
      registrarUpdatedAssigned: 'rgb(255, 165, 13)',
      pending: 'rgb(64, 169, 255)',
      pendingAssigned: 'rgb(24, 144, 255)',
    },
  },
}

export const DashboardPage: React.FC<IDashboard> = (props) => {
  const { keycloak } = useAuth()
  const t = useTranslation()
  const { realm } = props
  const dashboardTitle = 'Dashboard ' + t(`dashboard.${realm}`)
  const layout = React.useContext(LayoutContext)
  const isRegistrar = realm === 'registrar'
  const isRegistry = realm === 'registry'
  const hideAllCounters = isRegistrar && keycloak ? isRegistrarUserDarwin(keycloak) : false
  const pendingTitle = isRegistrar
    ? t('dashboard.status.pendingRegistrar')
    : t('dashboard.status.pendingRegistry')
  const registrarUpdatedTitle = isRegistrar ? (
    t('dashboard.status.registrarUpdated')
  ) : (
    <span>
      {t('dashboard.status.registrarUpdatedRegistryMain')}
      <span className="counter-title-sub">
        {t('dashboard.status.registrarUpdatedRegistryNote')}
      </span>
    </span>
  )
  React.useEffect(() => {
    layout.setLayoutElement(
      'BREADCRUMB',
      <Breadcrumb elements={[{ label: 'Dashboard', path: `/${realm}/dashboard` }]} />,
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const registrarInfo: IRegistrarInfo | undefined = keycloak?.tokenParsed as IRegistrarInfo
  const dashboardCountersEnabled = Boolean(
    keycloak?.authenticated &&
    keycloak?.tokenParsed &&
    (!isRegistrar || registrarInfo?.registrarId !== undefined)
  )

  /***
   * Assegna il valore della vista per le assegnazioni di richieste associate a quel utente/ruolo.
   * Se il valore è dato ed è compatibile con quello del ruolo allora viene associato altrimenti ne viene dato uno di default
   * @param value
   * @param role
   */

  const assignedToMeAssignment = {
    param: isRegistrar ? 'registrarAssignation' : 'registryAssignation',
    value: 'assignedToMe',
  } as IAssignmentParam

  const assignedAssignmentExpiring = {
    param: isRegistrar ? 'registrarAssignation' : 'registryAssignation',
    value: 'assignedToMe',
  } as IAssignmentParam

  const assignmentParam = isRegistrar ? 'registrarAssignation' : 'registryAssignation'
  const allAssignment = {
    param: isRegistrar ? 'registrarAssignation' : 'registryAssignation',
    value: 'all',
  } as IAssignmentParam

  const totalLabel = t('dashboard.label.total')
  const assignedLabel = t('dashboard.label.assignedToMe')

  const buildRequestRedirect = (status: string, assignmentValue: string, view?: string) => {
    const viewParam = view ? `&view=${view}` : ''
    return `/${realm}/requests?status=${status}${viewParam}&${assignmentParam}=${assignmentValue}`
  }

  const buildRequestPair = (
    status: string,
    view?: string,
    registrarId?: number
  ) => {
    const total = {
      label: totalLabel,
      kind: 'requests' as const,
      status: status as any,
      assignment: allAssignment,
      registrarId,
      redirect: buildRequestRedirect(status, 'all', view),
      enabled: dashboardCountersEnabled,
    }
    const assigned = {
      label: assignedLabel,
      kind: 'requests' as const,
      status: status as any,
      assignment: assignedToMeAssignment,
      registrarId,
      redirect: buildRequestRedirect(status, 'assignedToMe', view),
      highlight: true,
      enabled: dashboardCountersEnabled,
    }
    return hideAllCounters ? { primary: assigned } : { primary: total, secondary: assigned }
  }

  const buildExpiringPair = () => {
    const total = {
      label: totalLabel,
      kind: 'expiring' as const,
      assignment: allAssignment,
      days: 7,
      redirect: `/${realm}/requests_expiring?${assignmentParam}=all`,
      enabled: dashboardCountersEnabled,
    }
    const assigned = {
      label: assignedLabel,
      kind: 'expiring' as const,
      assignment: assignedAssignmentExpiring,
      days: 7,
      redirect: `/${realm}/requests_expiring?${assignmentParam}=assignedToMe`,
      highlight: true,
      enabled: dashboardCountersEnabled,
    }
    return hideAllCounters ? { primary: assigned } : { primary: total, secondary: assigned }
  }

  const pendingPair = buildRequestPair(
    'pending',
    'expirationDays,referenceContact',
    registrarInfo?.registrarId
  )
  const registrarUpdatedPair = buildRequestPair('registrarUpdated', 'expirationDays,referenceContact')
  const processingPair = buildRequestPair('processing')
  const completedPair = buildRequestPair('completed', undefined, registrarInfo?.registrarId)
  const processingExpiredPair = buildRequestPair('processingExpired')
  const expiredPair = buildRequestPair('expired')
  const rejectedPair = buildRequestPair('rejected')
  const cancelledPair = buildRequestPair('cancelled')
  const submittedPair = buildRequestPair('submitted', 'expirationDays,referenceContact')
  const expiringPair = buildExpiringPair()

  return (
    <Flex direction={'column'} justify={'center'} width={'100%'}>
      <PaperLayout asCard={true}>
        <Div>{dashboardTitle}</Div>
      </PaperLayout>
      <VerticalLayout>
        <DashboardSectionTitle
          label={t('dashboard.section.work')}
          icon={<InboxOutlined />}
        />
        <Div className="dashboard-grid dashboard-grid--2">
          {isRegistrar ? (
            <Can I={'View'} a={'PendingRequest'}>
            <DashboardCounter
              title={pendingTitle}
              icon={<>{PlayOrPause[realm].icons.pending}</>}
              scope={realm as NicScope}
              backgroundColor={PlayOrPause[realm].background.pending}
              className={`darwin${isRegistry ? ' pause' : ''}`}
              tip={t('dashboard.tip.pending')}
              primary={pendingPair.primary}
              secondary={pendingPair.secondary}
            />
            </Can>
          ) : (
            <Can I={'View'} a={'AllRegistrarUpdated'}>
            <DashboardCounter
              title={registrarUpdatedTitle}
              icon={<>{PlayOrPause[realm].icons.registrarUpdated}</>}
              scope={realm as NicScope}
              backgroundColor={PlayOrPause[realm].background.registrarUpdated}
              className={`darwin${isRegistrar ? ' pause' : ''}`}
              tip={t('dashboard.tip.registrarUpdated')}
              primary={registrarUpdatedPair.primary}
              secondary={registrarUpdatedPair.secondary}
            />
            </Can>
          )}
          <Can I={'View'} a={'ExpiringRequest'}>
            <DashboardCounter
              title={t('dashboard.status.expiring')}
              icon={
                <ExceptionOutlined
                  twoToneColor={'grey'}
                  style={{
                    fontSize: '2.2rem',
                    color: `${DashboardColorMapping.expiringSection.activeIconColor}`,
                  }}
                />
              }
              scope={realm as NicScope}
              backgroundColor={DashboardColorMapping.expiringSection.background}
              className={'darwin'}
              tip={t('dashboard.tip.expiring')}
              primary={expiringPair.primary}
              secondary={expiringPair.secondary}
            />
          </Can>
        </Div>
      </VerticalLayout>
      {isRegistrar ? (
        <VerticalLayout>
        <DashboardSectionTitle
          label={t('dashboard.section.monitor')}
          icon={<EyeOutlined />}
        />
          <Div className="dashboard-grid dashboard-grid--registrar-line">
            <Can I={'View'} a={'RegistrarUpdatedRequest'}>
              <DashboardCounter
                title={registrarUpdatedTitle}
                icon={<>{PlayOrPause[realm].icons.registrarUpdated}</>}
                scope={realm as NicScope}
                backgroundColor={PlayOrPause[realm].background.registrarUpdated}
                className={`darwin${isRegistrar ? ' pause' : ''} dashboard-span-2`}
                tip={t('dashboard.tip.registrarUpdated')}
                primary={registrarUpdatedPair.primary}
                secondary={registrarUpdatedPair.secondary}
              />
            </Can>
            <Can I={'View'} a={'CompletedRequest'}>
              <DashboardCounter
                title={t('dashboard.status.completed')}
                icon={<CheckOutlined style={{ fontSize: '2.2rem', color: 'white' }} />}
                scope={realm as NicScope}
                backgroundColor={DashboardColorMapping.successSection.background}
                className={'darwin'}
                tip={t('dashboard.tip.completed')}
                primary={completedPair.primary}
                secondary={completedPair.secondary}
              />
            </Can>
            <Can I={'View'} a={'ExpiredRequest'}>
              <DashboardCounter
                title={t('dashboard.status.expired')}
                icon={
                  <ClockCircleOutlined
                    twoToneColor={'red'}
                    style={{
                      fontSize: '2.2rem',
                      color: `${DashboardColorMapping.rejectOrFailureSection.activeIconColor}`,
                    }}
                  />
                }
                scope={realm as NicScope}
                backgroundColor={DashboardColorMapping.rejectOrFailureSection.background}
                className={'darwin'}
                tip={t('dashboard.tip.expired')}
                primary={expiredPair.primary}
                secondary={expiredPair.secondary}
              />
            </Can>
          </Div>
          <Div className="dashboard-grid dashboard-grid--2 dashboard-registrar-sub">
            <Can I={'View'} a={'ProcessingRequest'}>
              <DashboardCounter
                title={t('dashboard.status.processing')}
                icon={
                  <SyncOutlined
                    twoToneColor={'red'}
                    style={{
                      fontSize: '2.2rem',
                      color: `${DashboardColorMapping.processingSection.activeIconColor}`,
                    }}
                  />
                }
                scope={realm as NicScope}
                backgroundColor={DashboardColorMapping.processingSection.background}
                className={'darwin'}
                tip={t('dashboard.tip.processing')}
                primary={processingPair.primary}
                secondary={processingPair.secondary}
              />
            </Can>
            <Can I={'View'} a={'ProcessingExpiredRequest'}>
              <DashboardCounter
                title={t('dashboard.status.processingExpired')}
                icon={
                  <SyncOutlined
                    twoToneColor={'red'}
                    style={{
                      fontSize: '2.2rem',
                      color: `${DashboardColorMapping.processingExpiredSection.activeIconColor}`,
                    }}
                  />
                }
                scope={realm as NicScope}
                backgroundColor={DashboardColorMapping.processingExpiredSection.background}
                className={'darwin'}
                tip={t('dashboard.tip.processingExpired')}
                primary={processingExpiredPair.primary}
                secondary={processingExpiredPair.secondary}
              />
            </Can>
          </Div>
        </VerticalLayout>
      ) : (
        <VerticalLayout>
        <DashboardSectionTitle
          label={t('dashboard.section.monitor')}
          icon={<EyeOutlined />}
        />
          <Div className="dashboard-grid dashboard-grid--monitor">
            {/* SOLO PER ADMIN */}
            <Can I={'View'} a={'AllPendingRequest'}>
              <DashboardCounter
                title={pendingTitle}
                icon={<>{PlayOrPause[realm].icons.pending}</>}
                scope={realm as NicScope}
                backgroundColor={PlayOrPause[realm].background.pending}
                className={`darwin${isRegistry ? ' pause' : ''} dashboard-monitor-main`}
                tip={t('dashboard.tip.pending')}
                primary={pendingPair.primary}
                secondary={pendingPair.secondary}
              />
            </Can>
            <Can I={'View'} a={'ProcessingRequest'}>
              <DashboardCounter
                title={t('dashboard.status.processing')}
                icon={
                  <SyncOutlined
                    twoToneColor={'red'}
                    style={{
                      fontSize: '2.2rem',
                      color: `${DashboardColorMapping.processingSection.activeIconColor}`,
                    }}
                  />
                }
                scope={realm as NicScope}
                backgroundColor={DashboardColorMapping.processingSection.background}
                className={'darwin dashboard-monitor-top'}
                tip={t('dashboard.tip.processing')}
                primary={processingPair.primary}
                secondary={processingPair.secondary}
              />
            </Can>
            <Can I={'View'} a={'ProcessingExpiredRequest'}>
              <DashboardCounter
                title={t('dashboard.status.processingExpired')}
                icon={
                  <SyncOutlined
                    twoToneColor={'red'}
                    style={{
                      fontSize: '2.2rem',
                      color: `${DashboardColorMapping.processingExpiredSection.activeIconColor}`,
                    }}
                  />
                }
                scope={realm as NicScope}
                backgroundColor={DashboardColorMapping.processingExpiredSection.background}
                className={'darwin dashboard-monitor-bottom'}
                tip={t('dashboard.tip.processingExpired')}
                primary={processingExpiredPair.primary}
                secondary={processingExpiredPair.secondary}
              />
            </Can>
          </Div>
        </VerticalLayout>
      )}
      {!isRegistrar && (
        <>
          <VerticalLayout>
            <DashboardSectionTitle
              label={t('dashboard.section.history')}
              icon={<BarChartOutlined />}
            />
            <Div className="dashboard-grid dashboard-grid--5">
              <Can I={'View'} a={'CompletedRequest'}>
                <DashboardCounter
                  title={t('dashboard.status.completed')}
                  icon={<CheckOutlined style={{ fontSize: '2.2rem', color: 'white' }} />}
                  scope={realm as NicScope}
                  backgroundColor={DashboardColorMapping.successSection.background}
                  className={'darwin'}
                  tip={t('dashboard.tip.completed')}
                  primary={completedPair.primary}
                  secondary={completedPair.secondary}
                />
              </Can>
              <Can I={'View'} a={'SubmittedRequest'}>
                <DashboardCounter
                  title={t('dashboard.status.submitted')}
                  icon={
                    <LoginOutlined
                      twoToneColor={'gray'}
                      style={{
                        fontSize: '2.2rem',
                        color: `${DashboardColorMapping.initialSection.activeIconColor}`,
                      }}
                    />
                  }
                  scope={realm as NicScope}
                  backgroundColor={DashboardColorMapping.initialSection.background}
                  className={'darwin'}
                  tip={t('dashboard.tip.submitted')}
                  primary={submittedPair.primary}
                  secondary={submittedPair.secondary}
                />
              </Can>
              <Can I={'View'} a={'ExpiredRequest'}>
                <DashboardCounter
                  title={t('dashboard.status.expired')}
                  icon={
                    <ClockCircleOutlined
                      twoToneColor={'red'}
                      style={{
                        fontSize: '2.2rem',
                        color: `${DashboardColorMapping.rejectOrFailureSection.activeIconColor}`,
                      }}
                    />
                  }
                  scope={realm as NicScope}
                  backgroundColor={DashboardColorMapping.rejectOrFailureSection.background}
                  className={'darwin'}
                  tip={t('dashboard.tip.expired')}
                  primary={expiredPair.primary}
                  secondary={expiredPair.secondary}
                />
              </Can>
              <Can I={'View'} a={'RejectedRequest'}>
                <DashboardCounter
                  title={t('dashboard.status.rejected')}
                  icon={
                    <DislikeOutlined
                      twoToneColor={'red'}
                      style={{
                        fontSize: '2.2rem',
                        color: `${DashboardColorMapping.rejectOrFailureSection.activeIconColor}`,
                      }}
                    />
                  }
                  scope={realm as NicScope}
                  backgroundColor={DashboardColorMapping.rejectOrFailureSection.background}
                  className={'darwin'}
                  tip={t('dashboard.tip.rejected')}
                  primary={rejectedPair.primary}
                  secondary={rejectedPair.secondary}
                />
              </Can>
              <Can I={'View'} a={'CancelledRequest'}>
                <DashboardCounter
                  title={t('dashboard.status.cancelled')}
                  icon={
                    <StopOutlined
                      twoToneColor={'red'}
                      style={{
                        fontSize: '2.2rem',
                        color: `${DashboardColorMapping.rejectOrFailureSection.activeIconColor}`,
                      }}
                    />
                  }
                  scope={realm as NicScope}
                  backgroundColor={DashboardColorMapping.rejectOrFailureSection.background}
                  className={'darwin'}
                  tip={t('dashboard.tip.cancelled')}
                  primary={cancelledPair.primary}
                  secondary={cancelledPair.secondary}
                />
              </Can>
            </Div>
          </VerticalLayout>
        </>
      )}
    </Flex>
  )
}
